import { useEffect, useRef } from "react";
import { Ban, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, LoadingState, Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage, fmtDateTime } from "@/lib/api";

const JOB_TONE = { queued: "slate", running: "cyan", succeeded: "emerald", failed: "rose", timed_out: "rose", cancelled: "amber" };
const STAGE_COLOR = { pending: "bg-muted", running: "bg-cyan-400 animate-pulse", done: "bg-primary", skipped: "bg-slate-400/50", failed: "bg-rose-500" };

function JobCard({ job, onChanged }) {
  const { api, can } = useWorkspace();
  const act = (path, label) => async () => {
    try { await api.post(`/research-jobs/${job.id}/${path}`); toast.success(label); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const last = ["failed", "timed_out", "cancelled"].includes(job.status) || job.status === "queued" ? job.error_log?.[job.error_log.length - 1] : null;
  const isRetryWait = job.status === "queued" && last;
  return (
    <div className="panel space-y-3 p-4" data-testid={`job-${job.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={JOB_TONE[job.status]} testid={`job-status-${job.id}`}>{job.status.replace("_", " ")}</Pill>
        <span className="text-sm font-medium">{job.campaign_name}</span>
        <span className="font-mono text-[11px] text-muted-foreground">attempt {job.attempts}/{job.max_attempts} · £{Number(job.cost_gbp || 0).toFixed(4)} of £{Number(job.budget_gbp).toFixed(2)}</span>
        <span className="flex-1" />
        <span className="font-mono text-[11px] text-muted-foreground">{fmtDateTime(job.created_at)}</span>
        {can("write") && ["queued", "running"].includes(job.status) && <Button size="sm" variant="outline" onClick={act("cancel", "Cancellation requested")} data-testid={`job-cancel-${job.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Cancel</Button>}
        {can("write") && ["failed", "timed_out", "cancelled"].includes(job.status) && <Button size="sm" variant="outline" onClick={act("retry", "Job requeued")} data-testid={`job-retry-${job.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" />Retry</Button>}
      </div>
      <div className="flex gap-1" title="Pipeline stages">
        {job.stages.map((s) => <div key={s.key} title={`${s.label}: ${s.status}${s.message ? ` — ${s.message}` : ""}`} className={`h-1.5 flex-1 rounded-full ${STAGE_COLOR[s.status]}`} data-testid={`job-stage-${job.id}-${s.key}`} />)}
      </div>
      {(() => {
        const s = [...job.stages].reverse().find((x) => x.message && x.status !== "failed");
        const text = job.status === "succeeded" ? job.result_summary : s && `${s.label}: ${s.message}`;
        return text ? <p className="whitespace-pre-line break-words text-xs text-muted-foreground" data-testid={`job-summary-${job.id}`}>{text.replace(/; (?=[A-Z])/g, "\n")}</p> : null;
      })()}
      {last && (
        <p role="alert" className="rounded-md border border-rose-500/30 bg-rose-500/10 p-2.5 text-xs text-rose-700 dark:text-rose-300" data-testid={`job-error-${job.id}`}>
          <span className="font-semibold">{isRetryWait ? "Previous attempt failed — " : ""}{last.stage ? `Stage “${last.stage}”: ` : ""}</span>{last.message}
        </p>
      )}
    </div>
  );
}

export function JobsList({ campaignId, reloadKey, onSettled }) {
  const { data, loading, error, reload } = useFetch("/research-jobs", campaignId ? { campaign_id: campaignId } : {});
  const active = data?.some((j) => ["queued", "running"].includes(j.status));
  useEffect(() => { if (reloadKey) reload(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!active) return undefined; const id = setInterval(reload, 3000); return () => clearInterval(id); }, [active, reload]);
  const wasActive = useRef(false);
  useEffect(() => { if (wasActive.current && !active) onSettled?.(); wasActive.current = Boolean(active); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading && !data) return <LoadingState label="Loading research jobs…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data.length) return <div className="panel"><EmptyState title="No research jobs yet" description="Run a campaign to create a job. Each job is persisted with its stage status, attempts, costs and errors." testid="jobs-empty" /></div>;
  return <div className="space-y-3" data-testid="jobs-list">{data.map((j) => <JobCard key={j.id} job={j} onChanged={reload} />)}</div>;
}
